import { useMemo, useState } from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { meshChecks, meshStats, weldVertices, type Mesh } from "@/lib/mesh3d";
import { parseNumber } from "../converters/numberInput";
import { formatSize, isLengthUnit } from "./meshMeasure";

const nf = (n: number, d = 2) => (n !== 0 && Math.abs(n) < 0.01 ? n.toExponential(2) : n.toLocaleString(undefined, { maximumFractionDigits: d }));

/** Size, vertex/triangle counts and 3D-printing checks for a mesh whose coordinates are in `unitLabel`. */
export default function MeshReport({ mesh, unitLabel = "units" }: { mesh: Mesh; unitLabel?: string }) {
  const [bed, setBed] = useState({ x: "220", y: "220", z: "250" });
  const info = useMemo(() => {
    const welded = weldVertices(mesh);
    return { stats: meshStats(welded), checks: meshChecks(welded), removed: welded.removedDegenerate, rawVerts: mesh.vertices.length / 3 };
  }, [mesh]);
  const { stats, checks } = info;
  const mm = unitLabel === "mm";
  const b = [parseNumber(bed.x), parseNumber(bed.y), parseNumber(bed.z)];
  const fits = mm && b.every((n) => n !== null && n > 0) ? stats.size.every((s, i) => s <= (b[i] as number)) : null;
  return (
    <div className="space-y-3" data-testid="mesh-report">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-center">
        <div><p className="text-lg font-bold text-primary">{stats.vertexCount.toLocaleString()}</p><p className="text-xs text-muted-foreground">Vertices{info.rawVerts !== stats.vertexCount ? ` (${info.rawVerts.toLocaleString()} before merging)` : ""}</p></div>
        <div><p className="text-lg font-bold text-primary">{stats.triangleCount.toLocaleString()}</p><p className="text-xs text-muted-foreground">Triangles</p></div>
        <div className="col-span-2"><p className="text-sm font-medium" data-testid="mesh-bbox">{isLengthUnit(unitLabel) ? formatSize(stats.size, unitLabel) : `${nf(stats.size[0])} × ${nf(stats.size[1])} × ${nf(stats.size[2])} ${unitLabel}`}</p><p className="text-xs text-muted-foreground">Bounding box (X × Y × Z)</p></div>
      </div>
      <ul className="text-sm space-y-1">
        <li className={checks.watertight ? "text-green-600" : "text-amber-600"}>
          {checks.watertight ? "✓ Watertight: every edge joins exactly two faces, so a slicer can treat it as a solid." : `⚠ Not watertight: ${checks.openEdges.toLocaleString()} open edge${checks.openEdges === 1 ? "" : "s"}${checks.nonManifoldEdges ? ` and ${checks.nonManifoldEdges.toLocaleString()} edge(s) shared by 3+ faces` : ""}. Slicers may report holes.`}
        </li>
        {checks.watertight && <li>Volume {nf(checks.volume)} {unitLabel}³ · surface area {nf(checks.area)} {unitLabel}²{checks.flipped ? " · faces point inward (normals flipped)" : ""}</li>}
        {info.removed > 0 && <li className="text-muted-foreground">{info.removed.toLocaleString()} zero-area triangle(s) were ignored in these checks.</li>}
      </ul>
      {mm && (
        <div className="flex flex-wrap items-end gap-2 text-sm">
          <span className="text-xs text-muted-foreground">Print bed (mm):</span>
          {(["x", "y", "z"] as const).map((k) => (
            <div key={k}><Label className="text-[10px] uppercase text-muted-foreground">{k}</Label><Input aria-label={`Bed ${k} (mm)`} className="h-8 w-20" inputMode="decimal" value={bed[k]} onChange={(e) => setBed({ ...bed, [k]: e.target.value })} /></div>
          ))}
          {fits !== null && <span className={fits ? "text-green-600" : "text-amber-600"}>{fits ? "✓ Fits on the bed" : "⚠ Larger than the bed"}</span>}
        </div>
      )}
    </div>
  );
}
